import classNames from 'classnames';
import dynamic from 'next/dynamic';
import React from 'react';

import {GwgAccelerator} from '@udemy/career-academies';
import {useI18n} from '@udemy/i18n';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {BlockSkeleton} from '@udemy/react-reveal-components';
import {DynamicWebContentPageType} from '@udemy/shared-one-udemy-components';
import type {CMSConnectedPartnersBannerProps} from '@udemy/shared-one-udemy-components';

import {GrowWithGoogle} from 'src/components/grow-with-google/grow-with-google.react-component';
import {PlanComparisonFaqValuePlan} from 'src/components/plan-comparison-faq/plan-comparison-faq-value-plan.react-component';
import {PlanComparisonFaq} from 'src/components/plan-comparison-faq/plan-comparison-faq.react-component';
import {
    ComparePlanCta,
    PlanComparison,
} from 'src/components/plan-comparison/plan-comparison.react-component';
import {valueProps} from 'src/components/value-prop/constants';
import {VALUE_PLANSTARTER_PLAN} from 'src/lib/experimentation-constants';

import {ValueProp} from '../../components/value-prop/value-prop.react-component';
import {useIsGwgAvailable} from '../../hooks/useIsGwgAvailable';
import styles from './plan-comparison-body.module.less';

const PartnersBannerSkeleton = () => (
    <BlockSkeleton className={styles['partners-banner-skeleton']} />
);

const TestimonialsSkeleton = () => <BlockSkeleton className={styles['testimonials-skeleton']} />;

const ComparisonMatrixSkeleton = () => (
    <BlockSkeleton className={styles['comparison-matrix-skeleton']} />
);

const CMSConnectedPartnersBanner = dynamic<CMSConnectedPartnersBannerProps>(
    () =>
        import('@udemy/shared-one-udemy-components').then(
            (module) => module.CMSConnectedPartnersBanner,
        ),
    {ssr: false, loading: PartnersBannerSkeleton},
);

const TestimonialsCMSWrapper = dynamic(
    () => import('@udemy/react-cms-components').then((module) => module.TestimonialsCMSWrapper),
    {ssr: false, loading: TestimonialsSkeleton},
);

const ComparisonMatrix = dynamic(
    () =>
        import('src/components/comparison-matrix/comparison-matrix.react-component').then(
            (module) => module.ComparisonMatrix,
        ),
    {ssr: false, loading: ComparisonMatrixSkeleton},
);

export interface PlanComparisonBodyProps {
    showUBLA: boolean;
    allGwgAccelerators?: GwgAccelerator[];
}

export const PlanComparisonBody = ({showUBLA, allGwgAccelerators}: PlanComparisonBodyProps) => {
    const {gettext} = useI18n();
    const {featureVariantAssignments: features} = useFeatures([VALUE_PLANSTARTER_PLAN]);

    const isValuePlanExperimentEnabled =
        features.get(VALUE_PLANSTARTER_PLAN)?.configuration?.enableValuePlan ?? false;
    const isGwgAvailable = useIsGwgAvailable();
    const comparisonMatrixAnchorId = 'comparison-matrix-anchor';
    const onComparisonCtaClick = () => {
        const element = document.getElementById(comparisonMatrixAnchorId);
        if (element) {
            element.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    };
    return (
        <div className={classNames(styles['plan-comparison-body'])}>
            <section className={classNames('ud-container', styles['plan-comparison'])}>
                <PlanComparison showUBLA={showUBLA} />
                <div className={styles['comparison-matrix-cta']}>
                    <ComparePlanCta onClick={onComparisonCtaClick} />
                </div>
            </section>

            {isGwgAvailable && !isValuePlanExperimentEnabled && (
                <section className={styles['grow-with-google']}>
                    <GrowWithGoogle allGwgAccelerators={allGwgAccelerators} />
                </section>
            )}

            {isValuePlanExperimentEnabled && (
                <section className={'ud-container'}>
                    <h1 className={classNames('ud-heading-xxl', styles['value-props-heading'])}>
                        {gettext('Learning for your career and lifestyle')}
                    </h1>
                    {valueProps.map((valueProp) => (
                        <ValueProp
                            key={valueProp.id}
                            imgSrc={valueProp.imgSrc}
                            className="component-margin"
                            heading={valueProp.heading}
                            content={valueProp.content}
                        />
                    ))}
                </section>
            )}
            <section className={styles['partners-banner']}>
                <div className={classNames('ud-container')}>
                    <div className={styles['partners']}>
                        <CMSConnectedPartnersBanner
                            pageType={DynamicWebContentPageType.RequestDemoForm}
                        />
                    </div>
                </div>
            </section>
            <section className={styles['testimonials']}>
                <div className={classNames('ud-container')}>
                    <TestimonialsCMSWrapper />
                </div>
            </section>
            <section
                className={classNames('ud-container', styles['comparison-matrix'])}
                id={comparisonMatrixAnchorId}
            >
                <ComparisonMatrix />
            </section>
            <section className={styles['faq']}>
                <div className="ud-container">
                    {isValuePlanExperimentEnabled ? (
                        <PlanComparisonFaqValuePlan />
                    ) : (
                        <PlanComparisonFaq showUBLA={showUBLA} />
                    )}
                </div>
            </section>
        </div>
    );
};
